export const STARTERS = [
  {
    id: 'side-effects',
    title: 'Side effects this week',
    label: 'Between visits',
    text: "I'm in treatment. Help me sort what I'm feeling into what I should mention at my next oncology visit, and what is worth calling the clinic about today. I will describe what I'm noticing. Do not change any doses.",
  },
  {
    id: 'treatment',
    title: 'Walk me through this treatment',
    label: 'Treatment clarity',
    text: 'Walk me through this treatment the way an oncology clinic would explain it: what visits look like, common side effects, and the questions that get a clearer plan sooner. I will tell you the regimen. Do not invent a protocol or change doses.',
  },
  {
    id: 'appointment',
    title: 'Prep my next appointment',
    label: 'Clinic prep',
    text: 'Help me walk into my next oncology visit ready. I will share the cancer type, where I am in treatment, and what is worrying me. Give me a short list of questions I can actually ask in the room.',
  },
  {
    id: 'food',
    title: 'What to eat this cycle',
    label: 'Food and energy',
    text: 'I want practical food guidance during this cycle of cancer treatment: nausea, taste changes, mouth sores, and energy. I will share the regimen. Help me eat something I can keep down tonight, and tell me what to ask the team about nutrition support so I do not wait another week.',
  },
];

export const CHECKIN_EXAMPLES = [
  'Remind me to log today’s symptoms every evening at 8pm',
  'Ask me about energy, appetite, and pain every morning at 9am',
  'Weekly reflection on Sundays at noon: what to take to clinic',
];

export const ANALYSIS_KINDS = [
  { id: 'questions', title: 'Questions for clinic', blurb: 'What I would ask in the room, written the way I would say it.' },
  { id: 'symptoms', title: 'Symptom patterns', blurb: 'What has been recurring, and what is worth flagging.' },
  { id: 'treatment', title: 'Treatment themes', blurb: 'Open questions about the plan. Never a dose change.' },
  { id: 'caregiver', title: 'Caregiver load', blurb: 'Practical support for the person next to them.' },
];

export const COMPOSER_TOOLS = [
  {
    group: 'Ask Vani',
    items: [
      {
        id: 'side-effects',
        kind: 'prompt',
        icon: 'bell',
        title: 'Side effects',
        detail: 'What to mention, what to call about',
        text: STARTERS[0].text,
      },
      {
        id: 'treatment',
        kind: 'prompt',
        icon: 'doc',
        title: 'Treatment walkthrough',
        detail: 'Visits, side effects, questions to ask',
        text: STARTERS[1].text,
      },
      {
        id: 'appointment',
        kind: 'prompt',
        icon: 'chat',
        title: 'Prep my appointment',
        detail: 'A short list for the oncologist',
        text: STARTERS[2].text,
      },
      {
        id: 'food',
        kind: 'prompt',
        icon: 'heart',
        title: 'Food this cycle',
        detail: 'Nausea, taste changes, energy',
        text: STARTERS[3].text,
      },
      {
        id: 'labs',
        kind: 'prompt',
        icon: 'chart',
        title: 'Explain my labs',
        detail: 'Plain-language CBC, markers, scans',
        text: 'I will share lab values or attach a report. Explain the names and numbers in plain language for my oncology visit. What people often ask, and what I should confirm with my care team. Do not diagnose or change treatment.',
      },
      {
        id: 'meds',
        kind: 'prompt',
        icon: 'plus',
        title: 'Medications',
        detail: 'Chemo, supportive meds, questions',
        text: 'Help me understand the cancer medications I am on: what they are for, common side effects, and questions worth asking my oncology team so I get answers earlier. I will list the names. Do not change doses.',
      },
      {
        id: 'caregiver',
        kind: 'prompt',
        icon: 'heart',
        title: 'Caregiver help',
        detail: 'Support for the person next to me',
        text: 'I am a caregiver. Help me with practical support around oncology visits, side effects at home, and what to escalate to the clinic. I will share where we are in treatment.',
      },
      {
        id: 'biopsy',
        kind: 'prompt',
        icon: 'doc',
        title: 'Read this biopsy',
        detail: 'Pathology in plain language',
        text: 'I will share a biopsy or pathology report. Walk me through what the words mean, what people usually ask next, and what I should confirm with my oncologist. Do not diagnose or change treatment.',
      },
      {
        id: 'plan-review',
        kind: 'prompt',
        icon: 'search',
        title: 'Review this plan',
        detail: 'Questions before you decide',
        text: 'I have a diagnosis and a treatment plan. Help me see it clearly: what is standard to ask, what is worth another pair of eyes at clinic, and which questions get a clearer answer sooner. I will share the details. Do not invent a protocol or change doses.',
      },
    ],
  },
  {
    group: 'Shortcuts',
    items: [
      {
        id: 'upload',
        kind: 'upload',
        icon: 'doc',
        title: 'Attach a report',
        detail: 'PDF or photo of labs',
      },
      {
        id: 'care',
        kind: 'route',
        icon: 'search',
        title: 'Find care',
        detail: 'Mapped hospitals and oncologists',
        href: '/care',
      },
      {
        id: 'cost',
        kind: 'route',
        icon: 'chart',
        title: 'Cost calculator',
        detail: 'Estimate treatment costs',
        href: '/cost-calculator',
      },
      {
        id: 'treatment-options',
        kind: 'route',
        icon: 'doc',
        title: 'Treatment options',
        detail: 'Personalized themes by stage & biomarkers',
        href: '/treatment-options',
      },
      {
        id: 'checkins',
        kind: 'route',
        icon: 'bell',
        title: 'Symptoms',
        detail: 'Log severity between visits',
        href: '/check-ins',
      },
      {
        id: 'crisis',
        kind: 'route',
        icon: 'heart',
        title: 'Crisis help',
        detail: '911, 988, and cancer support lines',
        href: '/crisis',
      },
      {
        id: 'analysis',
        kind: 'route',
        icon: 'chart',
        title: 'Analysis',
        detail: 'Patterns from your chats',
        href: '/analysis',
        pro: true,
      },
      {
        id: 'bookmarks',
        kind: 'route',
        icon: 'bookmark',
        title: 'Bookmarks',
        detail: 'Answers you asked Vani to keep',
        href: '/bookmarks',
        pro: true,
      },
      {
        id: 'voice',
        kind: 'voice',
        icon: 'mic',
        title: 'Voice',
        detail: 'Talk instead of type',
        pro: true,
      },
    ],
  },
];

export const CRISIS = [
  {
    title: 'Emergency, United States',
    body: 'If this is a medical emergency: chest pain, trouble breathing, heavy bleeding, collapse, call 911 now.',
    action: 'Call 911',
    href: 'tel:911',
    meta: '24/7 • Emergency services',
  },
  {
    title: 'Suicide and Crisis Lifeline',
    body: 'Call or text 988. Confidential support if you are in emotional distress or thinking about suicide.',
    action: 'Call 988',
    href: 'tel:988',
    meta: '24/7 • Confidential',
  },
  {
    title: 'Cancer Support, ACS',
    body: 'The American Cancer Society can help with rides, lodging, and finding a US cancer center: 1-800-227-2345.',
    action: 'Call ACS',
    href: 'tel:18002272345',
    meta: '24/7 • United States',
  },
  {
    title: 'CancerCare counseling',
    body: 'Free professional counseling and support groups for people with cancer and caregivers: 1-800-813-4673.',
    action: 'Call CancerCare',
    href: 'tel:18008134673',
    meta: 'US • Confidential',
  },
  {
    title: 'India emergency',
    body: 'If you are in India and this is an emergency, call 108 or go to the nearest ER.',
    action: 'Call 108',
    href: 'tel:108',
    meta: 'India • Emergency',
  },
  {
    title: 'Find local help',
    body: 'If you are outside the US, use your local emergency number. Help is available, and we will stay with you after you are safe.',
    action: 'IASC directory',
    href: 'https://www.iasp.info/suicidalthoughts/',
    meta: 'International',
  },
];
